import { Dropdown, Menu, Button, Divider, Typography, Col, Row } from 'antd';
import {
	SettingOutlined,
	DeleteOutlined,
	CopyOutlined,
	LockOutlined,
	EyeOutlined,
	ClearOutlined,
	FieldTimeOutlined,
	BookOutlined,
	RocketFilled,
	InfoCircleOutlined,
} from '@ant-design/icons';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { Dashboard } from '@mui/icons-material';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { UserAccount } from '@/types/TAccounts';

interface Client {
	featuresSubAccount: any;
	id: string;
	number: string;
	password: string;
	subscribedProductId?: string;
	subscriptionStatus?: string;
	accountInfo: UserAccount;
}

interface ClientListProps {
	clients: Client[];
	setCurrentReceptionist: (data: { number: string; accountInfo: UserAccount }) => void;
	setDeleteReceptionistNoticeModal: (value: boolean) => void;
	setChangeMangedReceptionistPasswordModal: (value: boolean) => void;
	setClearAllLogsWarningModal: (value: boolean) => void;
	setResellerMinutesLimitationModal: (value: boolean) => void;
	previewCurrentClientMutation: any;
	currentClient: any;
	setCurrentClient: (client: any) => void;
	handleOpenDownloadModal: (value: string) => void;
	handleOpenManageCreditModal: (params: {
		subAccountId: string;
		clientSubscribedProductId?: string;
		clientSubscriptionStatus?: string;
		username: string;
	}) => void;
	isTrial: boolean;
	onConvert?: (number: string) => void;
	onShowClientDetails: (client: Client) => void;
}

const ClientList: React.FC<ClientListProps> = ({
	clients,
	setCurrentReceptionist,
	setDeleteReceptionistNoticeModal,
	setChangeMangedReceptionistPasswordModal,
	setClearAllLogsWarningModal,
	setResellerMinutesLimitationModal,
	previewCurrentClientMutation,
	currentClient,
	setCurrentClient,
	handleOpenDownloadModal,
	handleOpenManageCreditModal,
	isTrial,
	onConvert,
	onShowClientDetails,
}) => {
	const dispatch = useAppDispatch();

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	const handleOpenSubAccount = (client: Client) => {
		setCurrentClient(client.number);
		previewCurrentClientMutation.mutate(
			{ clientId: client.accountInfo?.id, agentId: client.number },
			{
				onSettled: () => {
					setCurrentClient(null);
				},
			},
		);
	};

	const menu = (client: Client) => (
		<Menu className="w-[250px]">
			{/* Header Section */}
			<Menu.Item
				key="header"
				disabled
				style={{ cursor: 'default', background: '#f5f5f5', padding: '10px' }}
			>
				<div>
					<Typography.Text strong className="block text-gray-600">
						USERNAME:{' '}
					</Typography.Text>
					<div className="flex justify-between items-center">
						{client?.accountInfo?.username}
						<Button
							type="link"
							icon={<CopyOutlined />}
							onClick={() => handleCopy(client.accountInfo?.username)}
							size="small"
						/>
					</div>
				</div>
				{/* <div className='mt-3'>
          <Typography.Text strong className='block text-gray-600'>
            PASSWORD:{' '}
          </Typography.Text>
          <div className='flex justify-between items-center'>
            {client.password}
            <Button
              type='link'
              icon={<CopyOutlined />}
              onClick={() => handleCopy(client?.accountInfo?.password)}
              size='small'
            />
          </div>
        </div> */}
			</Menu.Item>

			<Divider className="my-3" />
			<Menu.Item key="2" icon={<InfoCircleOutlined />} onClick={() => onShowClientDetails(client)}>
				See Client Details
			</Menu.Item>

			<Menu.Item
				key="3"
				icon={<Dashboard />}
				onClick={() => {
					dispatch(
						openModal({
							modalId: MODAL_IDS.FEATURE_GATING_CUSTOMIZE_MODAL,
							data: {
								isTrialForm: isTrial,
								number: client.number,
								featuresSubAccount: client.featuresSubAccount,
								isSubClient: true,
							},
						}),
					);
				}}
			>
				Feature Management
			</Menu.Item>

			<Menu.Item
				key="4"
				icon={<LockOutlined />}
				onClick={() => {
					setCurrentReceptionist({ number: client.number, accountInfo: client.accountInfo });
					setChangeMangedReceptionistPasswordModal(true);
				}}
			>
				Change Password
			</Menu.Item>

			<Menu.Item
				key="5"
				icon={<ClearOutlined />}
				onClick={() => {
					setCurrentReceptionist({ number: client.number, accountInfo: client.accountInfo });
					setClearAllLogsWarningModal(true);
				}}
			>
				Clear Logs
			</Menu.Item>

			<Menu.Item
				key="6"
				icon={<FieldTimeOutlined />}
				onClick={() => {
					setCurrentReceptionist({ number: client.number, accountInfo: client.accountInfo });
					setResellerMinutesLimitationModal(true);
				}}
			>
				Minutes Limitation
			</Menu.Item>

			{!isTrial &&
				client?.accountInfo?.inboundSubscribedProductId &&
				client?.accountInfo?.inboundSubscriptionStatus !== 'canceled' && (
					<Menu.Item
						key="8"
						icon={<CurrencyExchangeIcon />}
						onClick={() => {
							handleOpenManageCreditModal({
								subAccountId: client?.accountInfo?.id,
								clientSubscribedProductId: client?.accountInfo?.inboundSubscribedProductId,
								clientSubscriptionStatus: client?.accountInfo?.inboundSubscriptionStatus,
								username: client?.accountInfo?.username,
							});
						}}
					>
						Manage Credits
					</Menu.Item>
				)}
			<Menu.Item
				key="7"
				icon={<BookOutlined />}
				onClick={() => {
					handleOpenDownloadModal(client.number);
				}}
			>
				Download Logs
			</Menu.Item>

			<Divider className="my-3" />

			<Menu.Item
				key="9"
				icon={<DeleteOutlined />}
				danger
				onClick={() => {
					setCurrentReceptionist({ number: client.number, accountInfo: client.accountInfo });
					setDeleteReceptionistNoticeModal(true);
				}}
			>
				Delete Receptionist
			</Menu.Item>
		</Menu>
	);

	return (
		<Row gutter={[12, 12]} className="mt-2">
			{clients.map((client) => (
				<Col xs={24} sm={24} md={12} lg={12} xl={12} key={client.id}>
					<div className="bg-gray-100 p-4 rounded-lg flex items-center justify-between h-full">
						<div className="overflow-hidden max-w-[75%]">
							<h3 className="font-semibold text-lg my-0 text-ellipsis overflow-hidden whitespace-nowrap">
								{client.accountInfo.businessName}
							</h3>
							<p className="my-0 text-sm text-ellipsis overflow-hidden whitespace-nowrap">
								{client.accountInfo?.username}
							</p>
						</div>
						<div className="flex items-center gap-1">
							<Button
								icon={<EyeOutlined />}
								onClick={() => handleOpenSubAccount(client)}
								loading={currentClient === client.number}
							/>
							<Dropdown overlay={menu(client)} trigger={['click']}>
								<Button icon={<SettingOutlined />} />
							</Dropdown>
							{isTrial && (
								<Button
									type="primary"
									className="ml-2"
									onClick={() => {
										setCurrentClient(client.number);
										onConvert?.(client.number);
									}}
									loading={currentClient === client.number}
								>
									<RocketFilled /> Convert
								</Button>
							)}
						</div>
					</div>
				</Col>
			))}
		</Row>
	);
};

export default ClientList;
